import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { API_URL } from '../../utils/api';
import './DonateTab.css';

const RARITY_COLORS = {
  consumer: '#B0C3D9',
  industrial: '#5E98D9',
  mil_spec: '#4B69FF',
  restricted: '#8847FF',
  classified: '#D32CE6',
  covert: '#EB4B4B',
  contraband: '#E4AE39',
};

/**
 * Donate-tab i Profile — spelar-flöde för att donera ett skin till
 * drop-poolen. Speglar Discord ``/drop donate`` men som webbsida.
 *
 * Flöde:
 *   1. Vi hämtar botens Steam-trade-URL för aktiv guild
 *   2. Spelaren öppnar trade-URL:en i Steam och skickar skinnet som gåva
 *   3. Spelaren klickar "Jag har skickat trade offer"
 *   4. Backend accepterar inkommande gift-offers och registrerar dem
 *      som SkinPrize, attribuerat till spelaren via JWT
 */
export default function DonateTab() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { guildId } = usePermissions();

  const [tradeUrl, setTradeUrl] = useState('');
  const [tradeUrlLoading, setTradeUrlLoading] = useState(true);
  const [tradeUrlError, setTradeUrlError] = useState('');

  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [triggerError, setTriggerError] = useState('');

  const fetchTradeUrl = useCallback(async () => {
    if (!guildId) return;
    setTradeUrlLoading(true);
    setTradeUrlError('');
    try {
      const r = await fetch(
        `${API_URL}/api/settings/donate/bot-trade-url?guild_id=${guildId}`,
        { headers: getAuthHeaders() },
      );
      const data = await r.json();
      if (!r.ok) {
        setTradeUrlError(data.detail || data.error || t('donate.tradeUrlFailed'));
      } else {
        setTradeUrl(data.trade_url || '');
      }
    } catch {
      setTradeUrlError(t('donate.tradeUrlNetworkError'));
    } finally {
      setTradeUrlLoading(false);
    }
  }, [guildId, getAuthHeaders, t]);

  useEffect(() => {
    fetchTradeUrl();
  }, [fetchTradeUrl]);

  const handleTrigger = async () => {
    if (!guildId) {
      setTriggerError(t('donate.noGuild'));
      return;
    }
    setTriggerLoading(true);
    setTriggerResult(null);
    setTriggerError('');
    try {
      const r = await fetch(`${API_URL}/api/settings/donate/trigger`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ guild_id: guildId }),
      });
      const data = await r.json();
      if (!r.ok) {
        setTriggerError(data.detail || data.error || t('donate.verifyFailed'));
      } else {
        setTriggerResult(data);
      }
    } catch {
      setTriggerError(t('donate.verifyNetworkError'));
    } finally {
      setTriggerLoading(false);
    }
  };

  return (
    <div className="donate-tab">
      <div className="donate-intro">
        <h2>{t('donate.skinTitle')}</h2>
        <p>
          {t('donate.skinIntro')}
        </p>
      </div>

      <div className="donate-step">
        <div className="donate-step-num">1</div>
        <div className="donate-step-body">
          <h3>{t('donate.step1Title')}</h3>
          {tradeUrlLoading ? (
            <p className="donate-info">{t('donate.fetchingTradeUrl')}</p>
          ) : tradeUrlError ? (
            <p className="donate-error">{tradeUrlError}</p>
          ) : tradeUrl ? (
            <>
              <a
                href={tradeUrl}
                target="_blank"
                rel="noreferrer"
                className="donate-trade-link"
              >
                {t('donate.openTradeInSteam')}
              </a>
              <p className="donate-help">
                {t('donate.giftHelp')}
              </p>
            </>
          ) : (
            <p className="donate-info">{t('donate.tradeUrlNotConfigured')}</p>
          )}
        </div>
      </div>

      <div className="donate-step">
        <div className="donate-step-num">2</div>
        <div className="donate-step-body">
          <h3>{t('donate.step2Title')}</h3>
          <p className="donate-help">
            {t('donate.step2Help')}
          </p>
          <button
            type="button"
            className="donate-trigger-btn"
            onClick={handleTrigger}
            disabled={triggerLoading || !tradeUrl}
          >
            {triggerLoading ? t('donate.verifying') : t('donate.iSentOffer')}
          </button>
          {triggerError && (
            <p className="donate-error">{triggerError}</p>
          )}
        </div>
      </div>

      {triggerResult && (
        <div className="donate-result">
          {triggerResult.accepted === 0 ? (
            <>
              <h3>{t('donate.noPendingOffers')}</h3>
              <p>
                {t('donate.noPendingOffersHelp')}
              </p>
            </>
          ) : (
            <>
              <h3>{triggerResult.accepted === 1
                ? t('donate.skinsAcceptedOne', { count: triggerResult.accepted })
                : t('donate.skinsAcceptedMany', { count: triggerResult.accepted })}</h3>
              <ul className="donate-result-list">
                {(triggerResult.skins || []).map((s) => (
                  <li key={s.id}>
                    <span
                      className="donate-result-rarity"
                      style={{ background: RARITY_COLORS[s.rarity] || '#8847FF' }}
                    />
                    <span className="donate-result-name">
                      {s.stattrak && <span className="donate-result-st">ST </span>}
                      {s.display_name || s.name}
                    </span>
                    {s.estimated_value_sek && (
                      <span className="donate-result-value">
                        {s.estimated_value_sek} kr
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="donate-help">{t('donate.thanksContribution')}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
